"use client";

import {JSX, useMemo} from "react";

import {useTranslation} from "@/shared/i18n";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {ImageBlock} from "@/shared/ui/media";
import {ImageGalleryModal} from "@/shared/ui/media/image-block/components/image-gallery-modal";
import {useImageGallery} from "@/shared/ui/media/image-block/hooks/use-image-gallery";

import "./styles/featured-properties.scss";

export interface FeaturedPropertyImage {
    id: string | number;
    src: string;
    alt: string;
    showButton?: boolean;
}

export interface GalleryImage {
    src: string;
    alt: string;
}

export interface FeaturedPropertiesProps {
    title: string;
    virtualTourUrl?: string | null;
    mainImage: FeaturedPropertyImage;
    sideImages: Array<FeaturedPropertyImage>;
    galleryImages?: Array<GalleryImage>; // All photos for gallery
    className?: string;
}

export function FeaturedProperties({
    title,
    virtualTourUrl,
    mainImage,
    sideImages,
    galleryImages = [],
    className = "",
}: FeaturedPropertiesProps): JSX.Element {
    const {t} = useTranslation("rental");
    const {activeIndex, isOpen, openGallery, closeGallery} = useImageGallery();

    const mainButtonText = virtualTourUrl ? t("viewPhotosAnd3DTour") : t("seeAllPhotos");

    // Normalize gallery images for the shared modal
    const normalizedImages = useMemo(() => {
        if (galleryImages.length > 0) return galleryImages;
        return [{src: mainImage.src, alt: mainImage.alt}, ...sideImages.map((img) => ({src: img.src, alt: img.alt}))];
    }, [galleryImages, mainImage, sideImages]);

    const handleOpenGallery = (index: number): void => {
        openGallery(index);
    };

    return (
        <section className={`featured-properties ${className}`}>
            <ContainerBase className="container_base__padding--none">
                <div className="featured-properties__header">
                    <Typography as="h1" size={{desktop: 28, mobile: 20}} weight={600} className="featured-properties__title">
                        {title}
                    </Typography>
                </div>

                <div className="featured-properties__grid">
                    <div className="featured-properties__main-image">
                        <ImageBlock
                            width={830}
                            height={500}
                            src={mainImage.src}
                            alt={mainImage.alt}
                            showButton={mainImage.showButton}
                            buttonText={mainButtonText}
                            onButtonClick={() => handleOpenGallery(0)}
                            onImageClick={() => handleOpenGallery(0)}
                            priority={true}
                        />
                    </div>

                    <div className="featured-properties__side-images">
                        {sideImages.map((image, index) => (
                            <div key={image.id} className="featured-properties__side-image">
                                <ImageBlock
                                    width={600}
                                    height={400}
                                    src={image.src}
                                    alt={image.alt}
                                    showButton={image.showButton}
                                    buttonText={mainButtonText}
                                    onButtonClick={() => handleOpenGallery(index + 1)}
                                    onImageClick={() => handleOpenGallery(index + 1)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </ContainerBase>

            <ImageGalleryModal
                isOpen={isOpen}
                images={normalizedImages}
                activeIndex={activeIndex}
                onClose={closeGallery}
                virtualTourUrl={virtualTourUrl}
            />
        </section>
    );
}
